/*
 * Levendi — scroll reveal for the shared editorial page system.
 *
 * Content is visible by default. The section opts into motion synchronously
 * (see snippets/levendi-page-boot.liquid) by adding .lv-motion to its root, so
 * a JS failure, a blocked asset or prefers-reduced-motion all leave the page
 * fully readable and static rather than blank.
 */
(function () {
  'use strict';

  function init(root) {
    if (!root || !root.classList.contains('lv-motion')) return;
    if (root.dataset.lvReveal === 'on') return;
    root.dataset.lvReveal = 'on';

    var items = root.querySelectorAll('.lv-rv');
    if (!items.length) return;

    if (!('IntersectionObserver' in window)) {
      reveal(items);
      return;
    }

    var reported = false;
    var observer = new IntersectionObserver(
      function (entries) {
        reported = true;
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-in');
          observer.unobserve(entry.target);
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -30px 0px' }
    );

    items.forEach(function (item) {
      observer.observe(item);
    });

    /* Fail-open: a working observer reports on the first frame. If nothing is
       reported the environment isn't compositing, so show everything. */
    window.setTimeout(function () {
      if (reported) return;
      observer.disconnect();
      reveal(items);
    }, 2000);
  }

  function reveal(items) {
    items.forEach(function (item) {
      item.classList.add('is-in');
    });
  }

  function boot(scope) {
    (scope || document).querySelectorAll('.lv-page').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      boot();
    });
  } else {
    boot();
  }

  /* On templates with the slim utility bar, filter changes NAVIGATE instead
     of AJAX-morphing. The theme's facets JS re-renders the grid section with
     a view transition + skeleton, and on these templates that morph corrupts
     the custom header/bar DOM (stuck skeleton, missing controls). The theme
     still pushes the filtered URL synchronously on change — we simply land
     on it with a clean server render, same as the sort dropdown does. */
  (function () {
    if (!document.querySelector('.lv-ubar')) return;
    var reloading = false;

    function scheduleReload() {
      if (reloading) return;
      reloading = true;
      /* pushState of the new filter URL happens synchronously in the same
         change handler; one tick later the URL is the filtered one. */
      setTimeout(function () {
        window.location.reload();
      }, 60);
    }

    document.addEventListener(
      'change',
      function (e) {
        if (e.target && e.target.closest && e.target.closest('#filters-drawer')) {
          scheduleReload();
        }
      },
      true
    );

    /* "Clear" / "Clear all" are clicks, not input changes. */
    document.addEventListener(
      'click',
      function (e) {
        if (!e.target || !e.target.closest) return;
        var btn = e.target.closest('#filters-drawer button[on\\:click], .facets__status button');
        if (!btn) return;
        var action = btn.getAttribute('on:click') || '';
        if (action.toLowerCase().indexOf('clear') >= 0) scheduleReload();
      },
      true
    );
  })();

  /* Theme editor re-renders sections in place. */
  document.addEventListener('shopify:section:load', function (event) {
    boot(event.target);
  });

})();

/*
 * Levendi — collection description reveal.
 * 4 Sep 2026 (module), rewritten 5 Sep 2026 for mission "collection-header-v2".
 *
 * PRESENTATION ONLY. This module never reads, writes, splits, joins,
 * truncates or reorders the family's copy. It measures geometry, sets a
 * max-height on the container, and appends one control. Every sentence stays
 * in the DOM at every breakpoint and in every state — collapsed simply means
 * "clipped by overflow", which is why the text remains selectable by
 * find-in-page and is restored in full the moment the control is used.
 *
 * v2 changes, on Alex's direction:
 *   • Collapsed on DESKTOP as well as mobile. The old mobile-only gate and the
 *     769px CSS unclamp are gone.
 *   • The cut is a fixed TWO LINES, measured from the live line box of the
 *     block's first paragraph, not a paragraph boundary. v1 cut at the
 *     family's own paragraph edges, which on these descriptions meant five to
 *     nine lines showing — far more than Alex wants open.
 *   • A soft mask fade (CSS) makes the cut read as intentional.
 *
 * Fail-open by construction: if this module throws or never loads, no class
 * is added, the CSS clamp has nothing to bind to, and the description renders
 * in full exactly as it did before.
 */
(function () {
  'use strict';

  var SEL = '.lv-eband__desc';
  var LINES = 2; /* lines of the family's opening left showing when closed */
  var MIN_EXTRA = 0.8; /* less hidden than this and a control is not worth it */
  var LABEL_MORE = 'Read more';
  var LABEL_LESS = 'Read less';
  var uid = 0;

  /* The first paragraph is set larger than the body copy (the lead treatment
     in levendi-pages.css), so its own line box — not the block's inherited
     one — is what "two lines" means here. Falls back to the block's
     line-height, then to a font-size multiple, so a browser that reports
     "normal" still gets a sane number instead of NaN. */
  function lineHeight(el) {
    var target = el.firstElementChild || el;
    var cs = getComputedStyle(target);
    var lh = parseFloat(cs.lineHeight);
    if (lh > 0) return lh;
    lh = parseFloat(getComputedStyle(el).lineHeight);
    if (lh > 0) return lh;
    var fs = parseFloat(cs.fontSize);
    return fs > 0 ? fs * 1.6 : 0;
  }

  function control(el) {
    var next = el.nextElementSibling;
    if (next && next.classList.contains('lv-eband__more')) return next;

    if (!el.id) {
      uid += 1;
      el.id = 'lv-eband-desc-' + uid;
    }

    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'lv-eband__more';
    btn.textContent = LABEL_MORE;
    btn.setAttribute('aria-expanded', 'false');
    btn.setAttribute('aria-controls', el.id);
    btn.addEventListener('click', function () {
      toggle(el, btn);
    });
    if (el.parentNode) el.parentNode.insertBefore(btn, el.nextSibling);
    return btn;
  }

  function toggle(el, btn) {
    var open = btn.getAttribute('aria-expanded') === 'true';
    window.clearTimeout(el.lvSettle);

    if (open) {
      /* Collapsing from `none` cannot animate, so give the transition a real
         start value first, then flush layout before setting the end value. */
      el.style.maxHeight = el.scrollHeight + 'px';
      void el.offsetHeight;
      el.classList.remove('is-open');
      el.style.maxHeight = el.dataset.lvCut + 'px';
      btn.setAttribute('aria-expanded', 'false');
      btn.textContent = LABEL_MORE;
      return;
    }

    el.classList.add('is-open');
    el.style.maxHeight = el.scrollHeight + 'px';
    btn.setAttribute('aria-expanded', 'true');
    btn.textContent = LABEL_LESS;

    /* Settle to an unbounded height once the reveal is done. This is also the
       failsafe: a transition that never advances — a background tab, a
       throttled renderer, a device that drops the animation — would otherwise
       leave the block pinned at its collapsed height with the label saying
       "Read less". After this fires, expanded is expanded whatever the
       animation did. */
    el.lvSettle = window.setTimeout(function () {
      if (btn.getAttribute('aria-expanded') === 'true') el.style.maxHeight = 'none';
    }, 600);
  }

  function release(el) {
    var btn = el.nextElementSibling;
    window.clearTimeout(el.lvSettle);
    el.classList.remove('is-clamped');
    el.classList.remove('is-open');
    el.style.maxHeight = '';
    delete el.dataset.lvCut;
    if (btn && btn.classList.contains('lv-eband__more')) {
      btn.classList.remove('is-on');
      btn.setAttribute('aria-expanded', 'false');
      btn.textContent = LABEL_MORE;
    }
  }

  function apply(el) {
    var btn = el.nextElementSibling;
    var open =
      btn &&
      btn.classList.contains('lv-eband__more') &&
      btn.getAttribute('aria-expanded') === 'true';

    /* Measure unclamped, always — the previous state must not bias the read. */
    el.classList.remove('is-clamped');
    el.classList.remove('is-open');
    el.style.maxHeight = '';

    var lh = lineHeight(el);
    if (!lh || lh <= 0) return release(el);

    var full = el.scrollHeight;
    var cut = Math.round(LINES * lh);

    /* A description already at or near two lines is left alone: clamping it
       would buy nothing and hang a control off a block with nothing behind
       it. */
    if (full - cut < MIN_EXTRA * lh) return release(el);

    el.dataset.lvCut = cut;
    el.classList.add('was-clamped');
    el.classList.add('is-clamped');
    /* A block the reader had already opened stays open across a resize. */
    if (open) {
      el.classList.add('is-open');
      el.style.maxHeight = 'none';
    } else {
      el.style.maxHeight = cut + 'px';
    }

    var b = control(el);
    b.classList.add('is-on');
    b.setAttribute('aria-expanded', open ? 'true' : 'false');
    b.textContent = open ? LABEL_LESS : LABEL_MORE;
  }

  function run(scope) {
    try {
      var nodes = (scope || document).querySelectorAll(SEL);
      for (var i = 0; i < nodes.length; i++) apply(nodes[i]);
    } catch (e) {
      /* leave the copy fully visible */
    }
  }

  function ready(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn);
    } else {
      fn();
    }
  }

  ready(function () {
    run(document);
    /* Web fonts land after first paint and change the line count. */
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(function () {
        run(document);
      });
    }
  });

  var t;
  window.addEventListener('resize', function () {
    window.clearTimeout(t);
    t = window.setTimeout(function () {
      run(document);
    }, 180);
  });

  document.addEventListener('shopify:section:load', function (event) {
    run(event.target);
  });
})();
